// Simple Mapbox ZIP geocode helper
// NOTE: Client-side usage exposes token; for production proxy this request server-side.
// Returns [lng, lat] or null if not found.
export async function geocodeZip(zip: string): Promise<[number, number] | null> {
  const cleaned = zip.trim();
  if (!/^\d{5}$/.test(cleaned)) return null;
  const token = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN;
  if (!token) return null;
  // Improve accuracy: restrict to US, disable autocomplete, limit features
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${cleaned}.json?types=postcode&country=US&limit=5&autocomplete=false&access_token=${token}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
  const data = await res.json() as { features?: unknown[] };
  const features = Array.isArray(data.features) ? data.features : [];
    if (!features.length) return null;
    // Prefer exact postcode match in properties or text fields
  // Narrow feature shape with runtime guards
  type RawFeature = { properties?: { postcode?: string }; text?: string; place_name?: string; center?: unknown; bbox?: unknown };
  const exact = (features as RawFeature[]).find(f => f?.properties?.postcode === cleaned || f?.text === cleaned || f?.place_name?.startsWith(cleaned + ' '));
  const feature = exact || (features[0] as RawFeature | undefined);
  if (!feature || !Array.isArray(feature.center)) return null;
    let lng = feature.center[0];
    let lat = feature.center[1];
    // If bbox is present, compute its centroid for potentially better visual centering
    if (Array.isArray(feature.bbox) && feature.bbox.length === 4) {
      const [minLng, minLat, maxLng, maxLat] = feature.bbox;
      const centerInside = lng >= minLng && lng <= maxLng && lat >= minLat && lat <= maxLat;
      if (!centerInside) {
        // Fallback: center on the bbox when the reported center lies outside of it
        lng = (minLng + maxLng) / 2;
        lat = (minLat + maxLat) / 2;
      }
    }
    return [lng, lat];
  } catch {
    return null;
  }
}

// Detailed variant returning center + bbox (if available)
export interface ZipGeocodeResult {
  center: [number, number];
  bbox?: [number, number, number, number]; // [minLng, minLat, maxLng, maxLat]
  place_name?: string;
}

export async function geocodeZipDetailed(zip: string): Promise<ZipGeocodeResult | null> {
  const cleaned = zip.trim();
  if (!/^\d{5}$/.test(cleaned)) return null;
  const token = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN;
  if (!token) return null;
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${cleaned}.json?types=postcode&country=US&limit=1&autocomplete=false&access_token=${token}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
  const data = await res.json() as { features?: unknown[] };
  type RawFeature = { properties?: { postcode?: string }; text?: string; place_name?: string; center?: unknown; bbox?: unknown };
  const feature = (Array.isArray(data.features) ? data.features[0] : undefined) as RawFeature | undefined;
  if (!feature || !Array.isArray(feature.center)) return null;
  const gotZip = feature.text === cleaned || feature.properties?.postcode === cleaned || (typeof feature.place_name === 'string' && feature.place_name.includes(cleaned));
    if (!gotZip) return null; // ensure we truly matched the requested ZIP code
  const center: [number, number] = [feature.center[0], feature.center[1]];
  const bbox = Array.isArray(feature.bbox) && feature.bbox.length === 4 ? (feature.bbox as [number, number, number, number]) : undefined;
  return { center, bbox, place_name: typeof feature.place_name === 'string' ? feature.place_name : undefined };
  } catch {
    return null;
  }
}

export async function geocodeAny(query: string): Promise<[number, number] | null> {
  const cleaned = query.trim();
  if (!cleaned) return null;
  const token = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN;
  if (!token) return null;
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(cleaned)}.json?access_token=${token}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const feature = data.features?.[0];
    if (!feature || !Array.isArray(feature.center)) return null;
    return [feature.center[0], feature.center[1]];
  } catch {
    return null;
  }
}
